import type { Editor } from '@tiptap/react';
import { useCallback, useMemo } from 'react';

import { getTable } from '@/components/tiptap-editor/tiptap-node/table-node/lib/tiptap-table-utils';
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';
import { isExtensionAvailable } from '@/lib/tiptap-utils';

import { TrashIcon } from '@/components/tiptap-editor/tiptap-icons/trash-icon';

export interface UseTableDeleteConfig {
    editor?: Editor | null;
    tablePos?: number;
    hideWhenUnavailable?: boolean;
    onDeleted?: () => void;
}

const REQUIRED_EXTENSIONS = ['table'];

export const tableDeleteLabel = 'Delete table';

function canDeleteTable(editor: Editor | null, tablePos?: number): boolean {
    if (!editor || !editor.isEditable || !isExtensionAvailable(editor, REQUIRED_EXTENSIONS)) {
        return false;
    }

    try {
        const table = getTable(editor, tablePos);
        if (!table) return false;

        return editor.can().deleteTable();
    } catch {
        return false;
    }
}

function deleteEntireTable(editor: Editor | null, tablePos?: number): boolean {
    if (!canDeleteTable(editor, tablePos) || !editor) {
        return false;
    }

    try {
        return editor.chain().focus().deleteTable().run();
    } catch (error) {
        console.error('Error deleting table:', error);
        return false;
    }
}

function shouldShowDeleteTable({
    editor,
    hideWhenUnavailable,
    tablePos,
}: {
    editor: Editor | null;
    hideWhenUnavailable: boolean;
    tablePos?: number;
}): boolean {
    if (!editor || !editor.isEditable) return false;
    if (!isExtensionAvailable(editor, REQUIRED_EXTENSIONS)) return false;
    return hideWhenUnavailable ? canDeleteTable(editor, tablePos) : true;
}

export function useTableDelete(config: UseTableDeleteConfig) {
    const { editor: providedEditor, tablePos, hideWhenUnavailable = false, onDeleted } = config;

    const { editor } = useTiptapEditor(providedEditor);

    const isVisible = shouldShowDeleteTable({
        editor,
        hideWhenUnavailable,
        tablePos,
    });

    const canPerformDelete = canDeleteTable(editor, tablePos);

    const handleDelete = useCallback(() => {
        const success = deleteEntireTable(editor, tablePos);
        if (success) onDeleted?.();
        return success;
    }, [editor, tablePos, onDeleted]);

    const label = useMemo(() => tableDeleteLabel, []);

    return {
        isVisible,
        canDeleteTable: canPerformDelete,
        handleDelete,
        label,
        Icon: TrashIcon,
    };
}
